import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

interface DragDropContextType {
    isDragging: boolean;
    isLocalZoneActive: boolean;
    setLocalZoneActive: (active: boolean) => void;
    /** Mount-lifetime claim (TanitChat) — survives leave / resetDragState until release. */
    claimLocalZone: () => void;
    releaseLocalZone: () => void;
    resetDragState: () => void;
    dragFiles: File[] | null;
    dragType: string | null;
}

const DragDropContext = createContext<DragDropContextType>({
    isDragging: false,
    isLocalZoneActive: false,
    setLocalZoneActive: () => { },
    claimLocalZone: () => { },
    releaseLocalZone: () => { },
    resetDragState: () => { },
    dragFiles: null,
    dragType: null,
});

export const useDragDrop = () => useContext(DragDropContext);

export const DragDropProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isLocalZoneActive, setIsLocalZoneActive] = useState(false);
    const [dragFiles, setDragFiles] = useState<File[] | null>(null);
    const [dragType, setDragType] = useState<string | null>(null);

    const dragCounter = useRef(0);
    const dragBlocker = useRef(false);
    const localZoneClaims = useRef(0);
    const isLocalZoneActiveRef = useRef(false);

    const syncLocalZone = useCallback((active: boolean) => {
        isLocalZoneActiveRef.current = active;
        setIsLocalZoneActive(active);
    }, []);

    const isValidDrag = (e: DragEvent) => {
        const types = e.dataTransfer?.types || [];
        if (types.includes('polymech/internal')) return false;
        // Allow Files or Links (text/uri-list) or text/plain
        return types.includes('Files') || types.includes('text/uri-list') || types.includes('text/plain');
    };

    const resetDragState = useCallback(() => {
        setIsDragging(false);
        // Keep overlay suppressed while a mount claim is held (e.g. TanitChat).
        if (localZoneClaims.current === 0) syncLocalZone(false);
        else syncLocalZone(true);
        dragCounter.current = 0;

        dragBlocker.current = true;
        setTimeout(() => {
            dragBlocker.current = false;
        }, 100);
    }, [syncLocalZone]);

    useEffect(() => {
        const handleDragEnter = (e: DragEvent) => {
            if (dragBlocker.current) return;
            if (!isValidDrag(e)) return;
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current++;
            if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
                setIsDragging(true);
            }
        };

        const handleDragLeave = (e: DragEvent) => {
            if (!isValidDrag(e)) return;
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current--;
            if (dragCounter.current === 0) {
                setIsDragging(false);
                // Do not clear mount claims — only transient hover flags.
                if (localZoneClaims.current === 0) syncLocalZone(false);
            }
        };

        const handleDragOver = (e: DragEvent) => {
            if (!isDragging) return;
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            resetDragState();
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsDragging(false);
                if (localZoneClaims.current === 0) syncLocalZone(false);
                dragCounter.current = 0;
            }
        };

        window.addEventListener('dragenter', handleDragEnter);
        window.addEventListener('dragleave', handleDragLeave);
        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('drop', handleDrop);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('dragenter', handleDragEnter);
            window.removeEventListener('dragleave', handleDragLeave);
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('drop', handleDrop);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isDragging, resetDragState, syncLocalZone]);

    const setLocalZoneActive = useCallback((active: boolean) => {
        if (active) {
            syncLocalZone(true);
            return;
        }
        // Hover leave must not defeat a mount claim (TanitChat).
        if (localZoneClaims.current === 0) syncLocalZone(false);
    }, [syncLocalZone]);

    const claimLocalZone = useCallback(() => {
        localZoneClaims.current += 1;
        syncLocalZone(true);
    }, [syncLocalZone]);

    const releaseLocalZone = useCallback(() => {
        localZoneClaims.current = Math.max(0, localZoneClaims.current - 1);
        if (localZoneClaims.current === 0) syncLocalZone(false);
    }, [syncLocalZone]);

    return (
        <DragDropContext.Provider value={{
            isDragging,
            isLocalZoneActive,
            setLocalZoneActive,
            claimLocalZone,
            releaseLocalZone,
            resetDragState,
            dragFiles,
            dragType,
        }}>
            {children}
        </DragDropContext.Provider>
    );
};
